import { useState } from "react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Trash2 } from "lucide-react";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AI_REVIEW_STATUS, type AiCountPage, type AiCountTag } from "./ai-count-types";
import { AiDebugPanel } from "./ai-debug-panel";

type Mark = AiCountPage["proposals"][number];

function uncertaintyNote(mark: Mark) {
  if (mark.scope === "excluded") return "AI thinks this item is outside the takeoff scope.";
  if (mark.scope === "uncertain" || !mark.identityResolved) return "AI is unsure about this item. Check its tag before accepting.";
  if (!mark.geometryComplete) return "AI could not trace this item completely. Check its shape on the drawing.";
  return null;
}

export function AiCountReviewEditor({
  mark,
  busy,
  locked = false,
  debug = false,
  tags,
  onReview,
  onEditOnDrawing,
  onTraceManually,
  onDrawCutout,
  onRemoveCutout,
}: {
  mark: Mark;
  busy: boolean;
  /** The item's run is still measuring or its source changed; the backend rejects edits. */
  locked?: boolean;
  debug?: boolean;
  tags: AiCountTag[];
  onEditOnDrawing?: () => void;
  onTraceManually?: () => void;
  onDrawCutout?: (mark: Mark) => void;
  onRemoveCutout?: (mark: Mark, index: number) => void;
  onReview: (values: {
    decision: "accept" | "reject";
    x?: number;
    y?: number;
    tagId?: Id<"takeoffPilotExtractedTags">;
  }) => void;
}) {
  const [editX, setEditX] = useState("");
  const [editY, setEditY] = useState("");
  const [selectedTagId, setTagId] = useState(mark.tagId);
  const tagId = tags.some(tag => tag.tagId === selectedTagId) ? selectedTagId : mark.tagId;
  const geometry = mark.geometryJson ? JSON.parse(mark.geometryJson) as { kind: string; holes?: number[][][] } : undefined;
  const status = AI_REVIEW_STATUS[mark.reviewStatus];
  const note = uncertaintyNote(mark);
  const tagName = tags.find((tag) => tag.tagId === mark.tagId)?.rawValue ?? "Selected";
  const decide = (decision: "accept" | "reject") =>
    onReview({
      decision,
      ...(editX !== "" ? { x: Number(editX) / 100 } : {}),
      ...(editY !== "" ? { y: Number(editY) / 100 } : {}),
      ...(tagId !== mark.tagId ? { tagId } : {}),
    });
  return (
    <fieldset disabled={busy || locked} className="space-y-3 rounded-lg border-2 border-primary/40 bg-primary/5 p-3">
      <legend className="sr-only">Review {tagName} item</legend>
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold">Selected {tagName} Item</p>
        <StatusBadge status={status.status} label={status.label} showSpinner={false} />
      </div>
      {locked
        ? <p role="status" className="text-xs text-muted-foreground">Review unlocks when AI finishes measuring this page.</p>
        : note && <p className="text-xs text-muted-foreground">{note}</p>}
      {tags.length > 1 && (
        <div className="space-y-1">
          <Label htmlFor={`ai-mark-tag-${mark._id}`}>Item tag</Label>
          <Select value={tagId} onValueChange={(value) => setTagId(value as Id<"takeoffPilotExtractedTags">)}>
            <SelectTrigger id={`ai-mark-tag-${mark._id}`} className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>{tags.map((tag) => <SelectItem key={tag.tagId} value={tag.tagId}>{tag.rawValue}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => decide("accept")}>Accept and include</Button>
        <Button size="sm" variant="outline" onClick={() => decide("reject")}>Reject</Button>
      </div>
      {(onEditOnDrawing || onTraceManually || (geometry?.kind === "polygon" && onDrawCutout)) && (
        <div className="flex flex-wrap gap-2 border-t pt-3">
          {onEditOnDrawing && <Button size="sm" variant="ghost" onClick={onEditOnDrawing}>Edit on drawing</Button>}
          {onTraceManually && <Button size="sm" variant="ghost" onClick={onTraceManually}>Trace manually</Button>}
          {geometry?.kind === "polygon" && onDrawCutout && <Button size="sm" variant="ghost" onClick={() => onDrawCutout(mark)}>Draw a cutout</Button>}
        </div>
      )}
      {geometry?.kind === "polygon" && geometry.holes?.map((_, index) => (
        <div key={index} className="flex items-center justify-between gap-2 text-sm">
          <span>Cutout {index + 1}</span>
          {onRemoveCutout && <IconButton icon={Trash2} size="sm" label={`Remove cutout ${index + 1}`} onClick={() => onRemoveCutout(mark, index)} />}
        </div>
      ))}
      {debug && (
        <AiDebugPanel>
          <p>{mark.reviewStatus} · scope {mark.scope} · geometry {mark.geometryComplete ? "complete" : "incomplete"} · identity {mark.identityResolved ? "resolved" : "unresolved"}</p>
          <p>Source evidence: {mark.sourceEvidence ?? "Original source observation"}</p>
          <div className="flex flex-wrap gap-2">
            <Label className="flex items-center gap-1 text-xs">X (% from left)
              <Input className="w-24" type="number" min="0" max="100" step="any" placeholder={(mark.x * 100).toFixed(3)} value={editX} onChange={(e) => setEditX(e.target.value)} />
            </Label>
            <Label className="flex items-center gap-1 text-xs">Y (% from top)
              <Input className="w-24" type="number" min="0" max="100" step="any" placeholder={(mark.y * 100).toFixed(3)} value={editY} onChange={(e) => setEditY(e.target.value)} />
            </Label>
          </div>
        </AiDebugPanel>
      )}
    </fieldset>
  );
}
